import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Gauge, Clock, Server } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  up: { label: "All systems operational", color: "#34d399", Icon: CheckCircle2 },
  down: { label: "Service disruption", color: "#f87171", Icon: XCircle },
  unknown: { label: "Awaiting first health check", color: "#94a3b8", Icon: Activity },
};

export default function StatusPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${API}/status/${slug}`).then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => alive && setData(d)).catch(() => alive && setErr("This status page was not found."));
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [slug]);

  if (err) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center font-mono text-sm text-slate-400" data-testid="status-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center"><Activity className="animate-pulse text-[#00F0FF]" /></div>;

  const s = STATUS[data.current_status] || STATUS.unknown;
  const S = s.Icon;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="status-page">
      <div className="grain" />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-8 h-8 rounded-lg bg-[#0055FF] flex items-center justify-center font-display font-extrabold">A</div>
          <span className="font-display font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
        </div>

        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight" data-testid="status-title">{data.title}</h1>
        {data.description && <p className="text-slate-400 mt-2">{data.description}</p>}

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mt-8 rounded-2xl border p-6 flex items-center gap-4" style={{ borderColor: `${s.color}40`, background: `${s.color}10` }} data-testid="status-banner">
          <S size={28} style={{ color: s.color }} />
          <div>
            <p className="font-display font-bold text-xl" style={{ color: s.color }}>{s.label}</p>
            {data.url && <a href={data.url} target="_blank" rel="noreferrer" className="text-slate-400 text-sm hover:text-white">{data.url}</a>}
          </div>
        </motion.div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={Gauge} label="Uptime" value={data.uptime_pct != null ? `${data.uptime_pct}%` : "—"} testid="status-uptime" />
          <Metric icon={Clock} label="Avg latency" value={data.avg_latency_ms != null ? `${data.avg_latency_ms}ms` : "—"} />
          <Metric icon={ShieldCheck} label="SSL" value={data.ssl ? `${data.ssl.days_left}d` : "—"} tone={data.ssl?.status === "expiring" ? "#fbbf24" : undefined} />
          <Metric icon={Server} label="Checks" value={data.checks_recorded ?? 0} />
        </div>

        {/* Uptime bars */}
        {data.history?.length > 0 && (
          <div className="mt-8" data-testid="status-history">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recent checks</p>
            <div className="flex gap-0.5 items-end h-10">
              {data.history.slice().reverse().map((h, i) => (
                <div key={i} title={`${h.status} · ${h.latency_ms ?? "—"}ms`} className="flex-1 rounded-sm" style={{ height: "100%", background: h.status === "up" ? "#34d39955" : "#f87171aa" }} />
              ))}
            </div>
          </div>
        )}

        {/* Incidents */}
        <div className="mt-8" data-testid="status-incidents">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Incident history</p>
          {(!data.incidents || data.incidents.length === 0) ? (
            <p className="text-slate-500 text-sm inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400" /> No incidents recorded.</p>
          ) : (
            <div className="space-y-2">
              {data.incidents.map((inc, i) => (
                <div key={i} className="rounded-xl border border-red-500/25 bg-red-500/5 p-3 flex items-start gap-2.5">
                  <AlertTriangle size={14} className="text-red-400 mt-0.5" />
                  <div><p className="text-white text-sm">Downtime detected</p><p className="text-slate-500 text-xs">{inc.started_at} · {inc.detail}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deployment history */}
        {data.deployments?.length > 0 && (
          <div className="mt-8" data-testid="status-deployments">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Deployment history</p>
            <div className="space-y-1.5">
              {data.deployments.map((d, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-black/30 px-3 py-2 text-sm">
                  <span className="text-slate-300">{d.provider} · {d.environment}{d.is_rollback ? " · rollback" : ""}</span>
                  <span className="font-mono text-xs text-slate-500">{d.state} · {(d.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-12 text-center font-mono text-[10px] text-slate-600">Powered by ArcaCentum.ai · updated {(data.generated_at || "").slice(11, 19)} UTC</p>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-500"><Icon size={13} /><span className="font-mono text-[10px] uppercase tracking-widest">{label}</span></div>
      <p className="font-display font-bold text-2xl mt-1" style={tone ? { color: tone } : {}}>{value}</p>
    </div>
  );
}
